import type { IFormField } from "@/types/app";
import { useState } from "react";
import { Controller } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import { generateTemporaryPassword } from "@/lib/generate-password";

interface Props extends IFormField {
  errors: FieldErrors;
  control: Control<Record<string, unknown>>;
}

const PasswordField = ({
  label,
  name,
  placeholder,
  disabled,
  autoFocus,
  control,
  errors,
  description,
  showGenerateButton,
}: Props) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <div className="flex flex-col gap-2">
      {(label || showGenerateButton) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <Label
              htmlFor={name}
              className="text-sm font-medium text-card-foreground"
            >
              {label}
            </Label>
          )}
          {showGenerateButton && (
            <Controller
              control={control}
              name={name}
              render={({ field: { onChange } }) => (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 shrink-0 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                  disabled={disabled}
                  onClick={() => {
                    const generated = generateTemporaryPassword();
                    onChange(generated);
                    setShowPassword(true);
                  }}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  توليد تلقائي
                </Button>
              )}
            />
          )}
        </div>
      )}
      <div className="relative">
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <Input
              type={showPassword ? "text" : "password"}
              onChange={onChange}
              onBlur={onBlur}
              value={String(value || "")}
              ref={ref}
              placeholder={placeholder || ""}
              disabled={disabled || false}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus || false}
              autoComplete="new-password"
              id={name}
              className={`${
                errors[name]
                  ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20"
                  : "!border-border !shadow-none focus-visible:ring-0 focus-visible:border-border placeholder:text-placeholder"
              } h-10 !bg-transparent pe-10`}
              aria-invalid={errors[name] ? "true" : "false"}
            />
          )}
        />

        <button
          type="button"
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
          className="absolute end-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {errors[name] && (
        <p className="text-sm text-destructive mt-1">
          {errors[name]?.message as string}
        </p>
      )}
    </div>
  );
};

export default PasswordField;
